import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '@reservamos/user-menu-widget';
import { UserMenuWidget } from '@reservamos/user-menu-widget/react';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import isAccountWebTransport from '../../utils/accountWebTransport';
import useWhitelabelEnvs from '../../hooks/whitelabel/useWhitelabelEnvs';
import usePurchase from '../../hooks/store/usePurchase';
import { useReserhubAccount } from '../../context/ReserhubAccount';
import { CurrentLang } from '../../utils/lang';
import { isNativeAppWebView } from '../../utils/webview';
import { getCdsMenuProfile, clearCdsMenuProfile } from '../../services/commerce-api/cdsMenuProfile';

const DEFAULT_SITE_URL = '/';
const CDS_AUTH_METHOD = 'cds_frequent_passenger';

/**
 * Get a value that can be safely passed as a custom element attribute.
 * @param {*} value - Value to normalize.
 * @returns {string|undefined}
 */
function attributeValue(value) {
  if (value === null || value === undefined || value === '') return undefined;
  return String(value);
}

/**
 * Get the available Doters balance from a connected account.
 * @param {object} dotersAccount - Connected Doters account.
 * @returns {string|undefined}
 */
function getDotersBalance(dotersAccount) {
  return attributeValue(
    dotersAccount?.wallet?.balance?.available ??
      dotersAccount?.wallet?.balance ??
      dotersAccount?.balance,
  );
}

/**
 * Get the current site's URL for widget account links.
 * @param {string} siteUrl - Configured site URL.
 * @returns {string}
 */
function getSiteUrl(siteUrl) {
  if (siteUrl) return siteUrl;
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return DEFAULT_SITE_URL;
}

/**
 * ReserhubUserMenu component.
 * Render the shared user menu widget with data already loaded by the host app.
 * @returns {JSX.Element}
 */
export default function ReserhubUserMenu() {
  const { enabled, user, isUserLoading, isUserFetched, nextPurchase, logout, connectedAccounts } =
    useReserhubAccount();
  const features = useWhitelabelFeatures();
  const environment = useWhitelabelEnvs();
  const purchase = usePurchase();
  const [cdsWalletResult, setCdsWalletResult] = useState({
    wallet: null,
    userId: null,
    baseUrl: null,
  });
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';

  const isPurchasePending = pathname.includes('complete') && purchase?.state !== 'completed';

  const dotersAccount = connectedAccounts?.find((account) => account.provider === 'doters');
  const cdsEnabled = Boolean(
    environment.reserhub?.auth?.allowedAuthMethods?.includes(CDS_AUTH_METHOD),
  );
  const commerceApiBaseUrl = environment.reserhub?.api?.baseUrl;
  const sessionKey = attributeValue(user?.id);

  const completedCdsPurchase =
    purchase?.state === 'completed' && purchase?.cdsPointsRedemptionId != null
      ? purchase.token
      : null;

  useEffect(() => {
    let cancelled = false;
    const requestUserId = user?.id;
    const requestBaseUrl = commerceApiBaseUrl;

    setCdsWalletResult({ wallet: null, userId: requestUserId, baseUrl: requestBaseUrl });

    if (!cdsEnabled || !requestUserId) {
      clearCdsMenuProfile();
      return () => {
        cancelled = true;
      };
    }

    try {
      getCdsMenuProfile(
        JSON.stringify([requestBaseUrl, requestUserId, user?.sessionToken]),
        completedCdsPurchase,
      )
        .then((profile) => {
          if (!cancelled) {
            setCdsWalletResult({
              wallet: profile?.wallet ?? null,
              userId: requestUserId,
              baseUrl: requestBaseUrl,
            });
          }
        })
        .catch(() => {
          if (!cancelled) {
            setCdsWalletResult({
              wallet: null,
              userId: requestUserId,
              baseUrl: requestBaseUrl,
            });
          }
        });
    } catch {
      setCdsWalletResult({
        wallet: null,
        userId: requestUserId,
        baseUrl: requestBaseUrl,
      });
    }

    return () => {
      cancelled = true;
    };
  }, [cdsEnabled, commerceApiBaseUrl, user?.id, user?.sessionToken, completedCdsPurchase]);

  const cdsWallet =
    cdsWalletResult.userId === user?.id && cdsWalletResult.baseUrl === commerceApiBaseUrl
      ? cdsWalletResult.wallet
      : null;

  const widgetConfig = useMemo(() => {
    const config = {
      lang: CurrentLang().split('-')[0],
      hostApp: 'funnel',
      loading: isUserLoading || !isUserFetched,
      redirectToCurrentUrl: true,
      siteUrl: getSiteUrl(environment.funnelUrl),
      homeUrl: environment.homeUrl,
      firstName: attributeValue(user?.firstName),
      lastName: attributeValue(user?.lastName),
      upcomingPurchase:
        !isPurchasePending && nextPurchase ? attributeValue(nextPurchase.token) : undefined,
      dotersBalance: getDotersBalance(dotersAccount),
      dotersMemberId: attributeValue(dotersAccount?.profile?.memberId),
      dotersLevel: attributeValue(dotersAccount?.profile?.level),
      dotersProgramUrl: attributeValue(dotersAccount?.profile?.termsAndConditionsUrl),
      ...(features.DOTERS_ENABLED && { dotersEnabled: true }),
      ...(cdsEnabled && {
        cdsEnabled: true,
        cdsBalance: attributeValue(cdsWallet?.balance),
        cdsCategory: attributeValue(cdsWallet?.category),
        sessionKey,
      }),
    };

    return config;
  }, [
    dotersAccount,
    environment.funnelUrl,
    environment.homeUrl,
    features.DOTERS_ENABLED,
    cdsEnabled,
    cdsWallet,
    isPurchasePending,
    isUserFetched,
    isUserLoading,
    nextPurchase,
    sessionKey,
    user,
  ]);

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  // CDS Account uses the app's separate profile for login and account actions.
  // Keep the existing hide flag for legacy native transports and the normal widget in browsers.
  if (
    (enabled &&
      isAccountWebTransport(environment.brand) &&
      features.CDS_ACCOUNT_WEBVIEW_ENABLED === true) ||
    (features.HIDE_RESERHUB_LOGIN_ON_WEBVIEW && isNativeAppWebView())
  ) {
    return null;
  }

  return <UserMenuWidget config={widgetConfig} onLogout={handleLogout} />;
}
